import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiCalendar, FiTrendingUp, FiFileText } from 'react-icons/fi';
import { reportesService } from '../../services';
import useReportExport from '../../hooks/useReportExport';
import './ReportesCommon.scss';

const ReportesImpuestos = () => {
  const navigate = useNavigate();
  const { exportToPDF } = useReportExport();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Filtros
  const [period, setPeriod] = useState('month'); // week, month, quarter, year
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    loadReportData();
  }, [period, startDate, endDate]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        start_date: startDate || undefined,
        end_date: endDate || undefined
      };

      const response = await reportesService.getImpuestosCobrados(params);

      if (response.success) {
        setReportData(response.data);
      }
    } catch (error) {
      console.error('Error al cargar reporte de impuestos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    exportToPDF('Reporte de Impuestos Cobrados');
  };

  const periodOptions = [
    { value: 'week', label: 'Semana Actual' },
    { value: 'month', label: 'Mes Actual' },
    { value: 'quarter', label: 'Trimestre Actual' },
    { value: 'year', label: 'Año Actual' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  return (
    <div className="reportes-detail-page">
      {/* Header */}
      <div className="reportes-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/system/reportes')}>
            <FiArrowLeft />
            <span>Regresar a Reportes</span>
          </button>
          <h1 className="reportes-detail-title">Impuestos Cobrados</h1>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={handleExportPDF}>
            <FiFileText />
            <span>Exportar PDF</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="reportes-filters">
        <div className="filter-group">
          <label className="filter-label">
            <FiCalendar />
            <span>Período</span>
          </label>
          <select
            className="filter-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Desde</label>
          <input
            type="date"
            className="filter-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Fecha Hasta</label>
          <input
            type="date"
            className="filter-input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {/* Resumen */}
      {reportData?.summary && (
        <div className="reportes-summary">
          <div className="summary-card highlight">
            <span className="summary-label">Total Impuestos Cobrados</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_tax_collected)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Total Ventas</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_sales)}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Tasa Promedio</span>
            <span className="summary-value">{formatPercentage(reportData.summary.tax_rate_avg)}</span>
          </div>
          <div className="summary-card success">
            <span className="summary-label">
              <FiTrendingUp style={{ display: 'inline', marginRight: '6px' }} />
              Tendencia
            </span>
            <span className="summary-value">{reportData.summary.trend || 'N/A'}</span>
          </div>
        </div>
      )}

      {/* Tabla de Desglose por Período */}
      <div className="reportes-table-container">
        {loading ? (
          <div className="loading-state">Cargando reporte...</div>
        ) : reportData?.breakdown && reportData.breakdown.length > 0 ? (
          <table className="reportes-table">
            <thead>
              <tr>
                <th>Período</th>
                <th className="text-right">Total Ventas</th>
                <th className="text-right">Subtotal (sin IVA)</th>
                <th className="text-right">IVA Cobrado</th>
                <th className="text-right">Tasa Efectiva</th>
                <th className="text-right">Facturas</th>
              </tr>
            </thead>
            <tbody>
              {reportData.breakdown.map((row, index) => (
                <tr key={index}>
                  <td className="period-cell">{row.period_label}</td>
                  <td className="text-right font-bold">{formatCurrency(row.total_sales)}</td>
                  <td className="text-right">{formatCurrency(row.subtotal)}</td>
                  <td className="text-right success">{formatCurrency(row.tax_collected)}</td>
                  <td className="text-right">{formatPercentage(row.effective_tax_rate)}</td>
                  <td className="text-right">{row.invoice_count}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {reportData.summary && (
                <tr style={{ borderTop: '2px solid #4a3470', fontWeight: 'bold' }}>
                  <td>TOTAL</td>
                  <td className="text-right">{formatCurrency(reportData.summary.total_sales)}</td>
                  <td className="text-right">{formatCurrency(reportData.summary.total_sales - reportData.summary.total_tax_collected)}</td>
                  <td className="text-right success">{formatCurrency(reportData.summary.total_tax_collected)}</td>
                  <td className="text-right">{formatPercentage(reportData.summary.tax_rate_avg)}</td>
                  <td className="text-right">-</td>
                </tr>
              )}
            </tfoot>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay datos de impuestos para el período seleccionado.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportesImpuestos;
